/**
 * Pure tree-mutation helpers. The editor keeps a working copy of the
 * tree in React state; all updates go through these functions so they
 * never mutate the underlying object — TanStack Query's cached value
 * stays untouched, which means "Discard changes" is a single state set
 * back to the fetched tree.
 */

import { registry, type RegistryName } from "@dynamic-landing/components";
import { applyLayoutRecipe, moveNodeToParent, unwrapNode, wrapNodes } from "@dynamic-landing/layout";

import type {
  AddNodeOp,
  ApplyRecipeOp,
  ComponentNode,
  ComponentTree,
  MoveNodeOp,
  ProposalOp,
  RemoveNodeOp,
  SetPropOp,
  UnwrapNodeOp,
  WrapNodesOp,
} from "./types";
import { opKind } from "./types";

type RegistryEntry = (typeof registry)[RegistryName];

/** Returns a new tree with node `nodeId`'s prop set to `value`. */
export function setNodeProp(
  tree: ComponentTree,
  nodeId: string,
  propName: string,
  value: unknown,
): ComponentTree {
  return { root: setProp(tree.root, nodeId, propName, value) };
}

function setProp(
  node: ComponentNode,
  nodeId: string,
  propName: string,
  value: unknown,
): ComponentNode {
  if (node.node_id === nodeId) {
    const nextProps = { ...node.props };
    if (value === undefined || value === "") {
      delete nextProps[propName];
    } else {
      nextProps[propName] = value;
    }
    return { ...node, props: nextProps };
  }
  if (!node.children || node.children.length === 0) return node;
  let changed = false;
  const nextChildren = node.children.map((c) => {
    const updated = setProp(c, nodeId, propName, value);
    if (updated !== c) changed = true;
    return updated;
  });
  return changed ? { ...node, children: nextChildren } : node;
}

/** Cheap structural equality check — sufficient for "is the working tree
 *  different from the fetched tree?" dirty detection. */
export function treesEqual(a: ComponentTree, b: ComponentTree): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Apply a list of proposal ops in order. Pure: returns a new tree.
 * Used by the AI proposal Accept flow. Dispatches on the op kind:
 * set_prop, add_node, remove_node, move_node. Unknown ops are skipped.
 */
export function applyOps(tree: ComponentTree, ops: ProposalOp[]): ComponentTree {
  let cur = tree;
  for (const op of ops) {
    switch (opKind(op)) {
      case "set_prop": {
        const o = op as SetPropOp;
        cur = setNodeProp(cur, o.node_id, o.prop, o.value);
        break;
      }
      case "add_node": {
        const o = op as AddNodeOp;
        const reg = registry as Record<string, RegistryEntry | undefined>;
        const entry = reg[o.component];
        const newChild: ComponentNode = {
          node_id: o.new_node_id?.trim() || newNodeId(o.component),
          component: { name: o.component, version: entry?.version ?? "0.1.0" },
          props: o.props ?? {},
        };
        cur = addChildNode(cur, o.parent_node_id, newChild);
        break;
      }
      case "remove_node": {
        const o = op as RemoveNodeOp;
        cur = removeNode(cur, o.node_id);
        break;
      }
      case "move_node": {
        const o = op as MoveNodeOp;
        if (o.to_parent_id !== undefined) {
          cur = moveNodeToParent(cur, o.node_id, o.to_parent_id, o.index);
        } else if (o.delta === -1 || o.delta === 1) {
          cur = moveChild(cur, o.node_id, o.delta);
        }
        break;
      }
      case "apply_recipe": {
        // Route AI-driven restructure through the SAME pure recipe the gallery /
        // Design tab use (single source of truth). Pure + idempotent + lossless.
        const o = op as ApplyRecipeOp;
        cur = applyLayoutRecipe(cur, o.recipe_id);
        break;
      }
      case "wrap_nodes": {
        // GROUP existing siblings into a new container (Grid/Columns/Container/…).
        const o = op as WrapNodesOp;
        const reg = registry as Record<string, RegistryEntry | undefined>;
        const version = reg[o.component]?.version ?? "0.1.0";
        cur = wrapNodes(
          cur,
          o.node_ids,
          { name: o.component, version, props: o.props ?? {} },
          o.new_node_id?.trim() || newNodeId(o.component),
        );
        break;
      }
      case "unwrap_node": {
        // UNGROUP a container: promote its children into its parent.
        const o = op as UnwrapNodeOp;
        cur = unwrapNode(cur, o.node_id);
        break;
      }
    }
  }
  return cur;
}

/** Read the current value of a (node_id, prop) pair, or undefined. */
export function getNodeProp(
  tree: ComponentTree,
  nodeId: string,
  propName: string,
): unknown {
  return findNodeRO(tree.root, nodeId)?.props[propName];
}

function findNodeRO(node: ComponentNode, nodeId: string): ComponentNode | null {
  if (node.node_id === nodeId) return node;
  for (const c of node.children ?? []) {
    const hit = findNodeRO(c, nodeId);
    if (hit) return hit;
  }
  return null;
}

// ---------- structural mutations ----------

/** Append a new child to the node identified by parentNodeId. */
export function addChildNode(
  tree: ComponentTree,
  parentNodeId: string,
  newChild: ComponentNode,
): ComponentTree {
  return { root: insertChild(tree.root, parentNodeId, newChild) };
}

function insertChild(
  node: ComponentNode,
  parentId: string,
  child: ComponentNode,
): ComponentNode {
  if (node.node_id === parentId) {
    return { ...node, children: [...(node.children ?? []), child] };
  }
  if (!node.children || node.children.length === 0) return node;
  let changed = false;
  const next = node.children.map((c) => {
    const u = insertChild(c, parentId, child);
    if (u !== c) changed = true;
    return u;
  });
  return changed ? { ...node, children: next } : node;
}

/** Remove a node by node_id. Removing the root returns the tree unchanged. */
export function removeNode(tree: ComponentTree, nodeId: string): ComponentTree {
  if (tree.root.node_id === nodeId) return tree;
  return { root: dropChild(tree.root, nodeId) };
}

function dropChild(node: ComponentNode, targetId: string): ComponentNode {
  if (!node.children || node.children.length === 0) return node;
  const kept = node.children.filter((c) => c.node_id !== targetId);
  let changed = kept.length !== node.children.length;
  const next = kept.map((c) => {
    const u = dropChild(c, targetId);
    if (u !== c) changed = true;
    return u;
  });
  return changed ? { ...node, children: next } : node;
}

/** Move a child within its parent by delta (-1 up, +1 down). Clamped. */
export function moveChild(
  tree: ComponentTree,
  nodeId: string,
  delta: -1 | 1,
): ComponentTree {
  return { root: shiftChild(tree.root, nodeId, delta) };
}

function shiftChild(
  node: ComponentNode,
  targetId: string,
  delta: -1 | 1,
): ComponentNode {
  if (node.children && node.children.length > 0) {
    const idx = node.children.findIndex((c) => c.node_id === targetId);
    if (idx >= 0) {
      const newIdx = Math.min(Math.max(0, idx + delta), node.children.length - 1);
      if (newIdx === idx) return node;
      const copy = [...node.children];
      const [m] = copy.splice(idx, 1);
      if (m !== undefined) copy.splice(newIdx, 0, m);
      return { ...node, children: copy };
    }
    let changed = false;
    const next = node.children.map((c) => {
      const u = shiftChild(c, targetId, delta);
      if (u !== c) changed = true;
      return u;
    });
    return changed ? { ...node, children: next } : node;
  }
  return node;
}

/** Find the parent of a node, or null if it's the root / not found. */
export function findParent(
  tree: ComponentTree,
  nodeId: string,
): ComponentNode | null {
  return findParentIn(tree.root, nodeId);
}

function findParentIn(node: ComponentNode, targetId: string): ComponentNode | null {
  if (!node.children) return null;
  for (const c of node.children) {
    if (c.node_id === targetId) return node;
    const hit = findParentIn(c, targetId);
    if (hit) return hit;
  }
  return null;
}

/** Generate a short, readable node id. */
export function newNodeId(componentName: string): string {
  const slug = componentName.toLowerCase();
  const rand = Math.random().toString(36).slice(2, 7);
  return `${slug}_${rand}`;
}

/** Deep-clone a subtree, assigning fresh node ids throughout (props copied). */
export function cloneSubtree(node: ComponentNode): ComponentNode {
  return {
    ...node,
    node_id: newNodeId(node.component.name),
    props: { ...node.props },
    ...(node.children ? { children: node.children.map(cloneSubtree) } : {}),
  };
}

/** Insert `child` immediately AFTER the sibling identified by afterId. */
function insertAfter(node: ComponentNode, afterId: string, child: ComponentNode): ComponentNode {
  if (node.children && node.children.length > 0) {
    const idx = node.children.findIndex((c) => c.node_id === afterId);
    if (idx >= 0) {
      const next = [...node.children];
      next.splice(idx + 1, 0, child);
      return { ...node, children: next };
    }
    let changed = false;
    const mapped = node.children.map((c) => {
      const u = insertAfter(c, afterId, child);
      if (u !== c) changed = true;
      return u;
    });
    return changed ? { ...node, children: mapped } : node;
  }
  return node;
}

/** Duplicate a node as the next sibling of the original (fresh ids). Returns the
 *  new tree + the clone's id, or null when the node is the root / not found. */
export function duplicateNode(
  tree: ComponentTree,
  nodeId: string,
): { tree: ComponentTree; newId: string } | null {
  if (tree.root.node_id === nodeId) return null;
  const orig = findNodeRO(tree.root, nodeId);
  if (!orig) return null;
  const clone = cloneSubtree(orig);
  return { tree: { root: insertAfter(tree.root, nodeId, clone) }, newId: clone.node_id };
}
